// A refusal at the callout reaches the browser as the server's Authorization
// Violation and nothing more; the console then reads the cause itself from the
// auth service's refusal lookup with the inbox token it minted for the
// connection, and says it in one sentence (02-DESIGN/03-console/01-the-console.md
// § signing in; 02-DESIGN/01-identity/02-the-auth-service.md § refusals;
// decision 0016). The lookup answers once, and only what the person's own
// credential explains: a platform-state refusal names its class alone, and the
// card says so without guessing further.

import { refusalPath, type Refusal } from "../wire/auth.ts";

export type { Refusal };

/** The two classes a refusal has (impire-sdk-go/identity). */
export const CLASS_CLAIM_SHAPE = "claim-shape";
export const CLASS_PLATFORM_STATE = "platform-state";

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * readRefusal reads the refusal the auth service kept for an inbox token at
 * the origin, or nothing: none was kept, it was read already, or it expired.
 */
export async function readRefusal(
  origin: string,
  inbox: string,
  fetchFn: typeof fetch = fetch,
): Promise<Refusal | undefined> {
  const res = await fetchFn(origin + refusalPath(inbox), {
    cache: "no-store",
    headers: { accept: "application/json" },
  });
  if (res.status !== 200) {
    return undefined;
  }
  const body = (await res.json()) as unknown;
  if (!isRecord(body) || typeof body.class !== "string") {
    return undefined;
  }
  return body as unknown as Refusal;
}

/**
 * refusalSentence is the one sentence a refusal gets: for a workspace by its
 * name, or for none on the identity plane. Nothing where the lookup answered
 * nothing, so that the caller falls back to the two likely causes.
 */
export function refusalSentence(
  r: Refusal | undefined,
  name: string | undefined,
  /** The provider the workspace signs in through now, by its display name, for `provider-not-in-use`. */
  inUse?: string,
): string | undefined {
  if (r === undefined) {
    return undefined;
  }
  if (r.class !== CLASS_CLAIM_SHAPE) {
    return name === undefined
      ? "The platform did not admit the sign-in: something on its side stood in the way. Try again in a moment."
      : `${name} did not admit the sign-in: something on the platform's side stood in the way. Try again in a moment; if it keeps happening, ask an admin of ${name}.`;
  }
  switch (r.reason) {
    case "no-row":
      return name === undefined
        ? "No workspace has an access rule for this account."
        : `${name} has no access rule for this account. Ask an admin of ${name} to add you.`;
    case "expired":
      return "Your sign-in has expired. Sign in again.";
    case "not-yet-valid":
      return "Your sign-in is not valid yet: check this device's clock, then try again.";
    case "wrong-audience":
    case "wrong-issuer":
    case "no-trust-row":
    case "tenant-mismatch":
      return name === undefined
        ? "This sign-in is for a workspace's own provider. Sign in again from that workspace's page."
        : `This sign-in is not for ${name}. Sign in again from ${name}'s own page.`;
    case "provider-not-in-use":
      // A workspace signs in through one provider at a time (decision 0055).
      return name === undefined
        ? "This sign-in is not the one the workspace uses. Sign in again from that workspace's page."
        : inUse !== undefined
          ? `${name} signs in with ${inUse} now.`
          : `${name} signs in another way now. Sign in again from ${name}'s page.`;
    case "wrong-organisation":
      return name === undefined
        ? "This account is not in the organisation the workspace signs in with."
        : `This account is not in the organisation ${name} signs in with.`;
    case "tenant-required":
      return "This sign-in needs a workspace: open the workspace's page and sign in there.";
    case "tenant-unknown":
      return "There is no workspace by that name.";
    case "token-invalid":
      return "The provider did not recognise this sign-in. Sign in again.";
    default:
      return "The sign-in could not be verified. Sign in again.";
  }
}
